import { useApiMutation } from '../../api/mutations';

/**
 * Alle User-Lifecycle-Mutationen nach Spec [[orbit-specs/user-crud-admin]].
 *
 * Fixture-Mode liefert für jeden Call einen plausiblen Fake-Return — das
 * Frontend kann gegen die Hooks gebaut werden, ohne Backend verfügbar.
 */

const USERS_KEYS_TO_INVALIDATE = ['users', 'user', 'userTenants'];

/** POST /v1/admin/users — returns { user, generatedPassword? } */
export function useCreateUser() {
  return useApiMutation({
    live: (fetch, payload) =>
      fetch('/v1/admin/users', { method: 'POST', body: payload }),
    fixture: (payload) => {
      const id = 9000 + Math.floor(Math.random() * 1000);
      const customerNumber = `VTY-${String(id).padStart(8, '0')}`;
      const generatedPassword = payload.password
        ? null
        : `${randAlpha(2)}${randNum(2)}!${randAlpha(2)}${randNum(2)}_${randAlpha(4)}`;
      return {
        user: {
          id,
          customerNumber,
          email: payload.email,
          displayName: payload.displayName ?? null,
          role: payload.role ?? 'customer',
          accountType: payload.accountType ?? 'private',
          status: 'active',
          createdAt: new Date().toISOString(),
        },
        generatedPassword,
      };
    },
    invalidate: [USERS_KEYS_TO_INVALIDATE[0]],
    successMessage: (res) =>
      `Benutzer #${res.user.id} angelegt (${res.user.customerNumber}).`,
  });
}

/** PUT /v1/admin/users/:id — patch diff */
export function useUpdateUser() {
  return useApiMutation({
    live: (fetch, { id, patch }) =>
      fetch(`/v1/admin/users/${id}`, { method: 'PUT', body: patch }),
    fixture: ({ patch }) => ({ user: { ...patch } }),
    invalidate: USERS_KEYS_TO_INVALIDATE,
    successMessage: 'Änderungen gespeichert.',
  });
}

/** POST /v1/admin/users/:id/suspend { reasonCode, reasonNote? } */
export function useSuspendUser() {
  return useApiMutation({
    live: (fetch, { id, reasonCode, reasonNote }) =>
      fetch(`/v1/admin/users/${id}/suspend`, {
        method: 'POST',
        body: { reasonCode, reasonNote },
      }),
    invalidate: USERS_KEYS_TO_INVALIDATE,
    successMessage: 'Benutzer wurde gesperrt.',
  });
}

/** POST /v1/admin/users/:id/lift-suspension */
export function useLiftSuspension() {
  return useApiMutation({
    live: (fetch, { id }) =>
      fetch(`/v1/admin/users/${id}/lift-suspension`, { method: 'POST' }),
    invalidate: USERS_KEYS_TO_INVALIDATE,
    successMessage: 'Sperre wurde aufgehoben.',
  });
}

/** POST /v1/admin/users/:id/reverify-vat-id → { status, fields, reverseChargeEligible } */
export function useReverifyVatId() {
  return useApiMutation({
    live: (fetch, { id }) =>
      fetch(`/v1/admin/users/${id}/reverify-vat-id`, { method: 'POST' }),
    fixture: () => ({
      status: 'valid',
      fields: { country: 'DE', vatId: 'DE123456789', companyName: 'Beispiel GmbH', address: 'Marienplatz 1, 80331 München' },
      reverseChargeEligible: true,
    }),
    // Kein Toast — Result wird im Modal angezeigt
  });
}

/** POST /v1/admin/users/:id/anonymize → { jobId } */
export function useAnonymizeUser() {
  return useApiMutation({
    live: (fetch, { id, reason, confirmation }) =>
      fetch(`/v1/admin/users/${id}/anonymize`, {
        method: 'POST',
        body: { reason, confirmation },
      }),
    fixture: () => ({ jobId: `job_${Date.now()}` }),
    invalidate: USERS_KEYS_TO_INVALIDATE,
    successMessage: (res) =>
      `Anonymisierungs-Job ${res.jobId} eingeplant.`,
  });
}

/** POST /v1/admin/users/:id/tenants { brandOrigin } — idempotent */
export function useAddUserTenant() {
  return useApiMutation({
    live: (fetch, { id, brandOrigin }) =>
      fetch(`/v1/admin/users/${id}/tenants`, {
        method: 'POST',
        body: { brandOrigin },
      }),
    fixture: ({ brandOrigin }) => ({ brandOrigin }),
    invalidate: ['userTenants'],
    successMessage: (res) => `Brand "${res.brandOrigin}" zugewiesen.`,
  });
}

/** DELETE /v1/admin/users/:id/tenants/:tenantId */
export function useRevokeUserTenant() {
  return useApiMutation({
    live: (fetch, { id, tenantId }) =>
      fetch(`/v1/admin/users/${id}/tenants/${tenantId}`, { method: 'DELETE' }),
    invalidate: ['userTenants'],
    successMessage: 'Brand-Zuweisung entzogen.',
  });
}

/** Helpers */
function randAlpha(n) {
  const c = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let s = '';
  for (let i = 0; i < n; i += 1) s += c[Math.floor(Math.random() * c.length)];
  return s;
}
function randNum(n) {
  let s = '';
  for (let i = 0; i < n; i += 1) s += Math.floor(Math.random() * 10);
  return s;
}
